class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Listen for cart:refresh event (from mobile-sticky-atc and other integrations)
    document.addEventListener('cart:refresh', () => {
      this.refreshDrawer();
    });

    // Also listen for cart:open event
    document.addEventListener('cart:open', () => {
      this.refreshDrawer();
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    // Hide chat widget
    this.toggleChatWidget(false);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    // Show chat widget
    this.toggleChatWidget(true);
  }

  toggleChatWidget(show) {
    const chatLauncher = document.getElementById('ccw-launcher-container');
    if (chatLauncher) {
      chatLauncher.style.display = show ? '' : 'none';
    }
    // LINE friend button
    const lineBtn = document.getElementById('line-friend-button');
    if (lineBtn) {
      lineBtn.style.display = show ? '' : 'none';
    }
    // Hide third-party cart nudge / urgency widgets (e.g., "The Final Nudge" by ecomsend)
    // when the cart drawer is open so they don't clutter the UI.
    const nudgeSelectors = [
      '[class*="ecomsend__Popover__Widget"]',
      '[class*="nudge" i]',
      '[id*="nudge" i]',
      '[class*="final-nudge" i]',
      '[id*="final-nudge" i]',
    ];
    const matched = new Set();
    nudgeSelectors.forEach((sel) => {
      try {
        document.querySelectorAll(sel).forEach((el) => {
          if (!el.closest('cart-drawer')) matched.add(el);
        });
      } catch (e) {}
    });
    // Fallback: find any fixed/absolute elements that literally contain "Final Nudge" text
    document.querySelectorAll('div, aside, section, button, a').forEach((el) => {
      if (el.closest('cart-drawer')) return;
      if (matched.has(el)) return;
      const text = (el.textContent || '').trim();
      if (text && /final\s*nudge/i.test(text) && text.length < 80) {
        const pos = getComputedStyle(el).position;
        if (pos === 'fixed' || pos === 'absolute') matched.add(el);
      }
    });
    matched.forEach((el) => {
      el.style.display = show ? '' : 'none';
    });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    if (parsedState.sections && window.__renderCartSections) {
      window.__renderCartSections(parsedState.sections);
    } else {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  refreshDrawer() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = `${root}?sections=cart-drawer,cart-icon-bubble`;

    fetch(url)
      .then((r) => r.json())
      .then((sections) => {
        if (window.__renderCartSections) {
          window.__renderCartSections(sections);
        } else {
          if (sections['cart-drawer']) {
            const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
            const newDrawer = doc.querySelector('#CartDrawer');
            if (newDrawer) {
              document.getElementById('CartDrawer').innerHTML = newDrawer.innerHTML;
              this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
            }
          }
          if (sections['cart-icon-bubble']) {
            const doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
            const newBubble = doc.querySelector('.shopify-section');
            const bubble = document.getElementById('cart-icon-bubble');
            if (newBubble && bubble) bubble.innerHTML = newBubble.innerHTML;
          }
        }
        this.open();
      })
      .catch((err) => console.error('Cart drawer refresh failed:', err));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
